import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useLoaderData } from 'react-router-dom';
import { rewind } from '@turf/rewind';
import { Modal } from 'antd';
import * as d3 from 'd3';
import axios from 'axios';
import Map from '../../components/map/map';
import ChartCard from '../../components/chart-card/chart-card';
import MultyBarChart from '../../components/multy-bar-chart/multy-bar-chart';
import Context from '../../context/context';
import { ASPECT_TYPES, COUNTRY_NAME, GEOJSON } from '../../constants';
import PageTitle from '../../components/page-title/page-title';
import EstablishmentTypeToggle from '../../components/establishment-type-toggle/establishment-type-toggle';
import { scrollToTop } from '../../utils/helpers';

import './regions.css';

const SENTIMENT_COLORS = ['#5FD1A0', '#F27878'];
const KEYWORD_PREVIEW_COUNT = 10;

const Regions = () => {
  const {
    filteredReviews,
    selectedCategory,
    selectedPlatform,
    selectedSubCategory,
    selectedSentiment,
    selectedAspect,
    selectedRegion,
    updateSelectedRegion,
  } = useContext(Context);

  const geojsonData = useLoaderData();

  const [regionData, setRegionData] = useState({ visits: {}, sentiment: {} });
  const [mapDataType, setMapDataType] = useState('visits');
  const [establishmentSentimentData, setEstablishmentSentimentData] = useState();
  const [aspectSentimentData, setAspectSentimentData] = useState();
  const [keywordData, setKeywordData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dataRef = useRef();
  dataRef.current = selectedRegion;

  function toggleRegion(regionName) {
    if (dataRef.current.includes(regionName)) {
      updateSelectedRegion(
        dataRef.current.filter((region) => region !== regionName)
      );
    } else {
      updateSelectedRegion([...dataRef.current, regionName]);
    }
  }

  function mapRegionClick(_event, feature) {
    toggleRegion(feature.properties.shapeName);
  }

  function getVisitValueFunction(item) {
    return item || 0;
  }

  function getSentimentValueFunction(item) {
    if (!item) return 0;
    const sum = item.positive + item.negative;
    return sum ? (item.positive / sum - item.negative / sum) * 100 : 0;
  }

  function getVisitColors(data) {
    const valuesArray = Object.values(data).sort((a, b) => a - b);
    const step = Math.floor(
      (valuesArray[valuesArray.length - 1] - valuesArray[0]) / 10
    );
    const trasholdArray = [valuesArray[0]];
    for (let i = 1; i < 5; i++) {
      trasholdArray.push(trasholdArray[i - 1] + step * i);
    }
    return {
      trashold: trasholdArray,
      range: d3.schemeBlues[7],
    };
  }

  function getSentimentColors() {
    return {
      trashold: [-100, -75, -50, -25, -1, 0, 1, 25, 50, 75, 100],
      range: d3.schemeOranges[5]
        .reverse()
        .concat(['#ffffff'], d3.schemeGreens[5]),
    };
  }

  function getVisitTooltipText(item) {
    return item || 0;
  }

  function getSentimentTooltipText(item) {
    if (!item) return 0;
    return `Positive: ${item.positive}, Negative: ${item.negative}`;
  }

  useEffect(() => {
    const visitsData = filteredReviews.reduce((acc, review) => {
      const region = review.district;
      acc[region] = (acc[region] || 0) + 1;
      return acc;
    }, {});

    const sentimentData = filteredReviews.reduce((acc, review) => {
      const region = review.district;
      if (!acc[region]) {
        acc[region] = {
          negative: 0,
          positive: 0,
        };
      }
      review.aspectsentiment_set.forEach((aspect) => {
        if (aspect.label === 'Negative') {
          acc[region].negative += 1;
        } else {
          acc[region].positive += 1;
        }
      });
      return acc;
    }, {});

    const nextEstablishmentSentimentData = Object.entries(
      filteredReviews.reduce((acc, review) => {
        if (!acc[review.establishment_type]) {
          acc[review.establishment_type] = {
            negative: 0,
            positive: 0,
          };
        }

        review.aspectsentiment_set.forEach((aspect) => {
          if (selectedSentiment === 'Positive' && aspect.label === 'Positive') {
            acc[review.establishment_type].positive += 1;
          } else if (
            selectedSentiment === 'Negative' &&
            aspect.label === 'Negative'
          ) {
            acc[review.establishment_type].negative += 1;
          } else if (!selectedSentiment) {
            if (aspect.label === 'Positive') {
              acc[review.establishment_type].positive += 1;
            } else if (aspect.label === 'Negative') {
              acc[review.establishment_type].negative += 1;
            }
          }
        });

        return acc;
      }, {})
    ).map(([establishment, establishmentData]) => {
      const result = { group: establishment };
      Object.entries(establishmentData).forEach(([key, value]) => {
        result[key] = value;
      });
      return result;
    });
    nextEstablishmentSentimentData.columns = ['group', 'positive', 'negative'];

    const aspectMap = ASPECT_TYPES.reduce((acc, aspect) => {
      acc[aspect] = {
        positive: 0,
        negative: 0,
      };
      return acc;
    }, {});

    filteredReviews.forEach((review) => {
      review.aspectsentiment_set.forEach((aspect) => {
        if (selectedSentiment === 'Positive' && aspect.label === 'Positive') {
          aspectMap[aspect.aspect].positive += 1;
        } else if (
          selectedSentiment === 'Negative' &&
          aspect.label === 'Negative'
        ) {
          aspectMap[aspect.aspect].negative += 1;
        } else if (!selectedSentiment) {
          if (aspect.label === 'Positive') {
            aspectMap[aspect.aspect].positive += 1;
          } else if (aspect.label === 'Negative') {
            aspectMap[aspect.aspect].negative += 1;
          }
        }
      });
    });

    const nextAspectSentimentData = Object.entries(aspectMap).map(
      ([aspect, aspectData]) => {
        const result = { group: aspect };
        Object.entries(aspectData).forEach(([key, value]) => {
          result[key] = value;
        });
        return result;
      }
    );
    nextAspectSentimentData.columns = ['group', 'positive', 'negative'];

    const keywordFrequency = {};
    filteredReviews.forEach((review) => {
      review.keyword_set.forEach((keywordObj) => {
        const keywords = keywordObj.keywords.split(', ');
        keywords.forEach((keyword) => {
          const cleanKeyword = keyword.replace(/[[\]',]/g, '').trim();
          if (!cleanKeyword) return;
          keywordFrequency[cleanKeyword] =
            (keywordFrequency[cleanKeyword] || 0) + 1;
        });
      });
    });

    const nextKeywordData = Object.entries(keywordFrequency)
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 50);

    setRegionData({ visits: visitsData, sentiment: sentimentData });
    setEstablishmentSentimentData(nextEstablishmentSentimentData);
    setAspectSentimentData(nextAspectSentimentData);
    setKeywordData(nextKeywordData);
  }, [filteredReviews, selectedSentiment]);

  useEffect(() => {
    scrollToTop();
  }, []);

  const appliedFilters = {
    platform: selectedPlatform,
    category: selectedCategory,
    subcategories: selectedSubCategory,
    sentiment: selectedSentiment,
    aspects: selectedAspect,
    regions: selectedRegion,
  };

  const regionInsights = useMemo(() => {
    const source =
      mapDataType === 'visits' ? regionData.visits : regionData.sentiment;
    const names = Object.keys(source);

    const rows = names.map((name) => {
      if (mapDataType === 'visits') {
        const value = source[name] || 0;
        return {
          name,
          value,
          label: value.toLocaleString('en-US'),
          sortValue: value,
        };
      }

      const entry = source[name] || { positive: 0, negative: 0 };
      const score = getSentimentValueFunction(entry);
      const total = entry.positive + entry.negative;
      const positiveShare = total ? Math.round((entry.positive / total) * 100) : 0;

      return {
        name,
        value: Math.abs(score),
        label: `${positiveShare}% positive`,
        sortValue: score,
      };
    });

    return rows.sort((a, b) => b.sortValue - a.sortValue);
  }, [mapDataType, regionData]);

  const maxInsightValue = useMemo(() => {
    if (!regionInsights.length) return 1;
    return Math.max(...regionInsights.map((row) => row.value), 1);
  }, [regionInsights]);

  const keywordPreview = keywordData.slice(0, KEYWORD_PREVIEW_COUNT);
  const maxKeywordValue = useMemo(() => {
    if (!keywordData.length) return 1;
    return Math.max(...keywordData.map((item) => item.value), 1);
  }, [keywordData]);
  const mapTitle =
    mapDataType === 'visits'
      ? 'Region reviewers'
      : 'Sentiment distribution by region';

  const sentimentColorScale = useMemo(
    () =>
      d3
        .scaleOrdinal()
        .domain(['Positive', 'Negative'])
        .range(SENTIMENT_COLORS),
    []
  );

  return (
    <div className="regions-page">
      <PageTitle
        title="Regions"
        subtitle={`Geographic review patterns across ${COUNTRY_NAME}. Click a region on the map or in the list to filter.`}
      />

      <div className="regions-toolbar">
        <EstablishmentTypeToggle />
        <button
          type="button"
          className="regions-toolbar-action"
          onClick={() => setIsModalOpen(true)}
        >
          See top keywords
        </button>
      </div>

      <section className="regions-hero" aria-label="Regional map and insights">
        <div className="regions-map-panel">
          <div className="regions-map-chrome">
            <div>
              <p className="regions-eyebrow">Map</p>
              <h2 className="regions-panel-title">{mapTitle}</h2>
            </div>
            <div
              className="regions-layer-switch"
              role="radiogroup"
              aria-label="Map metric"
            >
              <button
                type="button"
                role="radio"
                aria-checked={mapDataType === 'visits'}
                className={`regions-layer-option${
                  mapDataType === 'visits' ? ' is-active' : ''
                }`}
                onClick={() => setMapDataType('visits')}
              >
                Reviewers
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={mapDataType === 'sentiment'}
                className={`regions-layer-option${
                  mapDataType === 'sentiment' ? ' is-active' : ''
                }`}
                onClick={() => setMapDataType('sentiment')}
              >
                Sentiment
              </button>
            </div>
          </div>

          <div className="regions-map-stage">
            <Map
              id="regions"
              data={regionData[mapDataType]}
              click={mapRegionClick}
              geodata={geojsonData}
              getValueFunction={
                mapDataType === 'visits'
                  ? getVisitValueFunction
                  : getSentimentValueFunction
              }
              getColorsFunction={
                mapDataType === 'visits' ? getVisitColors : getSentimentColors
              }
              getTooltipTextFunction={
                mapDataType === 'visits'
                  ? getVisitTooltipText
                  : getSentimentTooltipText
              }
              fileName={`region-distribution-${mapDataType}`}
              columns={
                mapDataType === 'visits'
                  ? { key: 'Number of Reviews' }
                  : {
                      positive: 'Positive Sentiments',
                      negative: 'Negative Sentiments',
                    }
              }
              filters={appliedFilters}
              title={mapTitle}
            />
          </div>
        </div>

        <aside className="regions-insights" aria-label="Region insights">
          <header className="regions-insights-header">
            <p className="regions-eyebrow">Region insights</p>
            <h2 className="regions-panel-title">
              {mapDataType === 'visits' ? 'By review volume' : 'By sentiment'}
            </h2>
            <p className="regions-insights-hint">
              Select a region to sync the map and companion charts.
            </p>
          </header>

          {regionInsights.length === 0 ? (
            <p className="regions-empty">No regional data for the current filters.</p>
          ) : (
            <ul className="regions-insights-list">
              {regionInsights.map((row) => {
                const isSelected = selectedRegion.includes(row.name);
                const width = `${Math.max((row.value / maxInsightValue) * 100, 4)}%`;

                return (
                  <li key={row.name}>
                    <button
                      type="button"
                      className={`regions-insight-row${
                        isSelected ? ' is-selected' : ''
                      }`}
                      onClick={() => toggleRegion(row.name)}
                      aria-pressed={isSelected}
                    >
                      <span className="regions-insight-meta">
                        <span className="regions-insight-name">{row.name}</span>
                        <span className="regions-insight-value">{row.label}</span>
                      </span>
                      <span className="regions-insight-track" aria-hidden="true">
                        <span
                          className="regions-insight-bar"
                          style={{ width }}
                        />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </section>

      <section
        className="regions-companions"
        aria-label="Sentiment companions"
      >
        <article className="regions-companion">
          <header className="regions-companion-header">
            <p className="regions-eyebrow">Companion</p>
            <h2 className="regions-panel-title">By establishment type</h2>
            <p className="regions-companion-copy">
              Positive and negative mentions across establishment categories.
            </p>
          </header>
          <ChartCard className="regions-companion-card">
            <MultyBarChart
              id="establishment-sentiment"
              data={establishmentSentimentData}
              color={SENTIMENT_COLORS}
              colorScale={sentimentColorScale}
              columns={{
                group: 'Type',
                positive: 'Positive',
                negative: 'Negative',
              }}
              fileName="establishments-sentiments"
              filters={appliedFilters}
              title="Establishment types"
            />
          </ChartCard>
        </article>

        <article className="regions-companion">
          <header className="regions-companion-header">
            <p className="regions-eyebrow">Companion</p>
            <h2 className="regions-panel-title">By aspect</h2>
            <p className="regions-companion-copy">
              How comfort, cleanliness, safety, and related aspects score.
            </p>
          </header>
          <ChartCard className="regions-companion-card">
            <MultyBarChart
              id="aspect-sentiment"
              data={aspectSentimentData}
              xTickTotated
              color={SENTIMENT_COLORS}
              colorScale={sentimentColorScale}
              columns={{
                group: 'Aspect',
                positive: 'Positive',
                negative: 'Negative',
              }}
              fileName="aspects-sentiments"
              filters={appliedFilters}
              title="Aspect types"
            />
          </ChartCard>
        </article>
      </section>

      <section className="regions-keywords" aria-label="Top keywords">
        <header className="regions-keywords-header">
          <div>
            <p className="regions-eyebrow">Language</p>
            <h2 className="regions-panel-title">Top keywords</h2>
          </div>
          <button
            type="button"
            className="regions-toolbar-action"
            onClick={() => setIsModalOpen(true)}
          >
            See all
          </button>
        </header>

        {keywordPreview.length === 0 ? (
          <p className="regions-empty">No keywords for the current filters.</p>
        ) : (
          <ul className="regions-keyword-cloud">
            {keywordPreview.map((item) => (
              <li key={item.key}>
                <span className="regions-keyword-chip">
                  <span className="regions-keyword-label">{item.key}</span>
                  <span className="regions-keyword-count">
                    {item.value.toLocaleString('en-US')}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal
        className="regions-keywords-modal"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
        centered
        title={
          <div className="regions-keywords-modal-heading">
            <p className="regions-eyebrow">Language</p>
            <h2 className="regions-keywords-modal-title">Top keywords</h2>
            <p className="regions-keywords-modal-subtitle">
              Most frequent terms in reviews for the current filters
              {keywordData.length > 0
                ? ` · ${keywordData.length.toLocaleString('en-US')} shown`
                : ''}
              .
            </p>
          </div>
        }
      >
        {keywordData.length > 0 ? (
          <ol className="regions-keywords-rank" data-testid="keyword-chart">
            {keywordData.map((item, index) => {
              const width = `${Math.max(
                (item.value / maxKeywordValue) * 100,
                4
              )}%`;

              return (
                <li key={item.key} className="regions-keywords-rank-item">
                  <span className="regions-keywords-rank-index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="regions-keywords-rank-body">
                    <div className="regions-keywords-rank-meta">
                      <span className="regions-keywords-rank-label">{item.key}</span>
                      <span className="regions-keywords-rank-value">
                        {item.value.toLocaleString('en-US')}
                      </span>
                    </div>
                    <span className="regions-keywords-rank-track" aria-hidden="true">
                      <span
                        className="regions-keywords-rank-bar"
                        style={{ width }}
                      />
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="regions-empty">No keyword data available.</p>
        )}
      </Modal>
    </div>
  );
};

Regions.propTypes = {};

Regions.defaultProps = {};

export default Regions;

export async function regionsLoader() {
  const geojsonData = await axios.get(GEOJSON);
  geojsonData.data.features = geojsonData.data.features.map((f) => {
    return rewind(f, { reverse: true });
  });
  return geojsonData.data;
}
